import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Video, Copy, Check, Loader2, Mail, ChevronDown, Lock, AlertTriangle, RotateCcw } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useServiceGovernance } from '@/hooks/useServiceGovernance';
import { useEntitlement } from '@/hooks/useEntitlement';
import { useInterviewRetakeLimit } from '@/hooks/useInterviewRetakeLimit';
import { SERVICE_KEYS } from '@/lib/serviceKeys';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { buildRecruitmentInterviewUrl } from '@/lib/candidateInterviewLinks';
import { createOrGetInterviewSession } from '@/lib/forkhire/interview/interviewSessions';
import {
  aiInterviewCandidateNotificationIdentity,
  sendManualAiInterviewInvitation,
} from '@/lib/forkhire/interview/aiInterviewInvitationIdentity';

interface SendInterviewLinkProps {
  applicationId: string;
  stage: string;
  jobId?: string;
  candidateEmail?: string;
  candidateName?: string;
  size?: 'sm' | 'default' | 'icon';
  compact?: boolean;
}

export function SendInterviewLinkButton({ applicationId, stage, jobId, candidateEmail, candidateName, size = 'sm', compact = false }: SendInterviewLinkProps) {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [sent, setSent] = useState(false);
  const actionInFlightRef = useRef(false);
  const queryClient = useQueryClient();
  const interviewGovernance = useServiceGovernance('ai_interviews');
  const hasAiInterviews = interviewGovernance.allowed;
  const interviewEntitlement = useEntitlement(SERVICE_KEYS.AI_INTERVIEWS);
  const { maxRetakes, isLoading: retakeLimitLoading } = useInterviewRetakeLimit();

  // Check if interview config exists for this job
  const { data: interviewConfig, isLoading: configLoading } = useQuery({
    queryKey: ['interview-config-check', jobId],
    enabled: !!jobId && hasAiInterviews,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('interview_configs')
        .select('id, status, questions_json, interview_expiry_days, interview_type')
        .eq('job_id', jobId!)
        .eq('status', 'published')
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) return null;
      return data;
    },
    staleTime: 60 * 1000,
  });

  const hasPublishedConfig = interviewConfig &&
    Array.isArray(interviewConfig.questions_json) &&
    (interviewConfig.questions_json as unknown[]).length > 0;

  // Check if an active interview session exists for this application
  const { data: hasActiveSession } = useQuery({
    queryKey: ['interview-session-exists', applicationId],
    enabled: !!applicationId && hasAiInterviews && !!hasPublishedConfig,
    queryFn: async () => {
      const { data } = await supabase
        .from('interview_sessions')
        .select('id, expires_at')
        .eq('application_id', applicationId)
        .in('status', ['invited', 'in_progress'])
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      return Boolean(data && (!data.expires_at || new Date(data.expires_at).getTime() > Date.now()));
    },
    staleTime: 30_000,
  });

  // Check how many completed sessions exist (for attempt labeling)
  const { data: completedSessionCount } = useQuery({
    queryKey: ['interview-completed-count', applicationId],
    enabled: !!applicationId && hasAiInterviews && !!hasPublishedConfig,
    queryFn: async () => {
      const { count } = await supabase
        .from('interview_sessions')
        .select('id', { count: 'exact', head: true })
        .eq('application_id', applicationId)
        .in('status', ['completed', 'scored', 'expired', 'incomplete', 'cancelled', 'failed']);
      return count ?? 0;
    },
    staleTime: 30_000,
  });

  // Retake governance: check if more retakes are allowed
  const completedCount = completedSessionCount ?? 0;
  // maxRetakes = null means unlimited, 0 means no retakes, N means N retakes allowed
  const canRetake = maxRetakes === null || completedCount < maxRetakes;

  // Allow sending interview links from shortlisted or interviewed stages
  if (stage !== 'shortlisted' && stage !== 'interviewed') return null;

  const createNewSession = async (forceNew = false) => {
    if (!hasPublishedConfig || !interviewConfig) return null;
    try {
      const newSession = await createOrGetInterviewSession(applicationId, forceNew);
      if (!newSession) {
        toast.error('Failed to create interview session.');
        return null;
      }
      queryClient.invalidateQueries({ queryKey: ['interview-session-exists', applicationId] });
      queryClient.invalidateQueries({ queryKey: ['interview-completed-count', applicationId] });
      queryClient.invalidateQueries({ queryKey: ['interview-sessions'] });
      queryClient.invalidateQueries({ queryKey: ['interview-candidates'] });
      queryClient.invalidateQueries({ queryKey: ['active-interview-sessions'] });
      return newSession;
    } catch (createErr) {
      console.error('Failed to create interview session:', createErr);
      const message = createErr instanceof Error && createErr.message.includes('active plan allowance or wallet credits')
        ? 'This action requires an active plan allowance or wallet credits.'
        : 'Failed to create interview session.';
      toast.error(message);
      return null;
    }
  };

  const getInterviewLink = async (forceNew = false) => {
    if (forceNew) {
      const session = await createNewSession(true);
      if (!session) return null;
      return { link: buildRecruitmentInterviewUrl(session.token), token: session.token, sessionNumber: session.session_number, sessionId: session.id, expiresAt: session.expires_at };
    }

    // The governed RPC is the single source of truth. It reuses the exact
    // invited/in-progress session and creates an initial session only when no
    // prior attempt exists. Direct table reads can be stale or RLS-limited.
    const session = await createNewSession(false);

    if (!session) {
      toast.error(hasPublishedConfig ? t('hire.interviewLink.noActiveSession') : t('hire.interviewLink.configureQuestionsTitle'));
      return null;
    }

    if (session.expires_at && new Date(session.expires_at) < new Date()) {
      toast.error('Interview link has expired. Create a new attempt.');
      return null;
    }

    return { link: buildRecruitmentInterviewUrl(session.token), token: session.token, sessionNumber: session.session_number, sessionId: session.id, expiresAt: session.expires_at };
  };

  const handleCopyLink = async (forceNew = false) => {
    if (actionInFlightRef.current) return;
    actionInFlightRef.current = true;
    setLoading(true);
    try {
      const result = await getInterviewLink(forceNew);
      if (!result) return;

      try {
        await navigator.clipboard.writeText(result.link);
        setCopied(true);
        toast.success(forceNew ? 'Retake interview link copied' : 'Interview link copied to clipboard');
        setTimeout(() => setCopied(false), 2000);
      } catch (clipboardErr) {
        window.prompt('Copy interview link:', result.link);
        toast.success('Interview link is ready to copy');
      }
    } catch {
      console.error('Failed to get interview link');
      toast.error('Failed to get interview link');
    } finally {
      actionInFlightRef.current = false;
      setLoading(false);
    }
  };

  const handleSendEmail = async (forceNew = false) => {
    if (!candidateEmail) {
      toast.error('No candidate email available');
      return;
    }
    if (actionInFlightRef.current) return;
    actionInFlightRef.current = true;
    setLoading(true);
    const resendActionId = crypto.randomUUID();
    try {
      const result = await getInterviewLink(forceNew);
      if (!result) return;

      const { data: applicationContext, error: contextError } = await supabase
        .from('applications')
        .select('candidates(name, email), jobs(title, org_id, organizations(name))')
        .eq('id', applicationId)
        .single();
      if (contextError || !applicationContext) {
        throw new Error('AI Interview invitation context is unavailable');
      }
      const contextCandidate = applicationContext.candidates as {
        name?: string | null;
        email?: string | null;
      } | null;
      const contextJob = applicationContext.jobs as {
        title?: string | null;
        org_id?: string | null;
        organizations?: { name?: string | null } | null;
      } | null;
      const recipientEmail = contextCandidate?.email?.trim() || candidateEmail;
      const resolvedCandidateName = contextCandidate?.name?.trim() || candidateName?.trim() || 'Candidate';
      const resolvedJobTitle = contextJob?.title?.trim();
      const resolvedCompanyName = contextJob?.organizations?.name?.trim();
      if (!recipientEmail || !resolvedJobTitle || !resolvedCompanyName) {
        throw new Error('AI Interview invitation requires candidate, job, and company context');
      }

      const intId = result.sessionNumber ? `INT-${String(result.sessionNumber).padStart(5, '0')}` : '';
      const intRef = intId ? ` (Ref: ${intId})` : '';
      const communicationLanguage = (i18n.resolvedLanguage ?? i18n.language).startsWith('ar') ? 'ar' : 'en';
      const expiresAt = result.expiresAt
        ? new Intl.DateTimeFormat(communicationLanguage === 'ar' ? 'ar-SA' : 'en-GB', {
          dateStyle: 'medium',
          timeStyle: 'short',
          timeZone: 'Asia/Riyadh',
        }).format(new Date(result.expiresAt))
        : '';

      // 1) Send email
      const delivery = await sendManualAiInterviewInvitation({
        sessionId: result.sessionId,
        resendActionId,
        invoke: (functionName, options) => supabase.functions.invoke(functionName, options),
        body: {
          to_email: recipientEmail,
          type: 'interview_invite',
          application_id: applicationId,
          org_id: contextJob?.org_id ?? null,
          communication_language: communicationLanguage,
          subject: communicationLanguage === 'ar'
            ? `دعوة مقابلة ذكاء اصطناعي — ${resolvedJobTitle}`
            : `AI Interview Invitation — ${resolvedJobTitle}`,
          body: communicationLanguage === 'ar'
            ? `مرحباً ${resolvedCandidateName}،\n\nندعوك لإكمال مقابلة ذكاء اصطناعي لوظيفة ${resolvedJobTitle} لدى ${resolvedCompanyName}.${intId ? ` مرجع المقابلة: ${intId}` : ''}\n\n${result.link}\n\nمع التحية،\n${resolvedCompanyName}`
            : `Dear ${resolvedCandidateName},\n\nYou are invited to complete an AI interview for ${resolvedJobTitle} at ${resolvedCompanyName}.${intId ? ` Interview reference: ${intId}` : ''}\n\n${result.link}\n\nBest regards,\n${resolvedCompanyName}`,
          variables: {
            '{{candidate_name}}': resolvedCandidateName,
            '{{job_title}}': resolvedJobTitle,
            '{{company_name}}': resolvedCompanyName,
            '{{interview_token}}': result.token,
            '{{expires_at}}': expiresAt,
          },
          platform_template_variables: {
            candidate_name: resolvedCandidateName,
            job_title: resolvedJobTitle,
            company_name: resolvedCompanyName,
            interview_token: result.token,
            expires_at: expiresAt,
          },
          platform_template_key: 'candidate.interview_invited',
          platform_template_portal_scope: 'forkhire',
          platform_template_audience: 'candidate',
        },
      });
      if (!delivery.providerAccepted) throw new Error('AI Interview invitation was not accepted by the mail provider');

      // 2) Send ForkSeek in-app notification (if candidate is linked)
      try {
        const { data: candidate } = await supabase
          .from('candidates')
          .select('seek_candidate_number')
          .eq('email', recipientEmail)
          .maybeSingle();

        if (candidate?.seek_candidate_number) {
          // Candidate is a ForkSeek user — look up their candidate_id
          const { data: seekProfile } = await supabase
            .from('candidate_profiles')
            .select('candidate_id')
            .eq('candidate_number', candidate.seek_candidate_number)
            .maybeSingle();

          if (seekProfile?.candidate_id) {
            // Fetch job title for notification context
            const { data: app } = await supabase
              .from('applications')
              .select('job_id, jobs(title, org_id, organizations(name))')
              .eq('id', applicationId)
              .maybeSingle();

            const relatedJob = app?.jobs as {
              title?: string | null;
              organizations?: { name?: string | null } | null;
            } | null;
            const jobTitle = relatedJob?.title || resolvedJobTitle;
            const orgName = relatedJob?.organizations?.name || resolvedCompanyName;

            const { error: notificationError } = await supabase.from('candidate_notifications').insert({
              candidate_id: seekProfile.candidate_id,
              notification_type: 'stage_shortlisted',
              title: 'AI Interview Invitation',
              body: `You've been invited to an AI interview for ${jobTitle} at ${orgName}.${intId ? ` Reference: ${intId}` : ''}`,
              payload_json: {
                interview_link: result.link,
                job_title: jobTitle,
                org_name: orgName,
                cta_label: 'Start AI Interview',
                ...aiInterviewCandidateNotificationIdentity(result.sessionId),
              },
            });
            if (notificationError && notificationError.code !== '23505') {
              console.warn('[hire-interview] forkseek_notification_failed');
            }
          }
        }
      } catch {
        // Non-blocking: notification failure shouldn't break the flow
        console.warn('[hire-interview] forkseek_notification_failed');
      }

      // Track link_sent_at for grace-period governance
      if (result.sessionId) {
        await supabase.from('interview_sessions')
          .update({ link_sent_at: new Date().toISOString() })
          .eq('id', result.sessionId);
      }

      setSent(true);
      toast.success(
        forceNew
          ? t('hire.interviewLink.newAttemptAccepted', { email: recipientEmail })
          : t('hire.interviewLink.invitationAccepted', { email: recipientEmail }),
        { description: t('hire.interviewLink.deliveryNotGuaranteed') },
      );
      setTimeout(() => setSent(false), 3000);
    } catch {
      console.error('Failed to send invitation');
      toast.error(t('hire.interviewLink.sendFailed'));
    } finally {
      actionInFlightRef.current = false;
      setLoading(false);
    }
  };

  const hasCompletedSessions = (completedSessionCount ?? 0) > 0;
  const nextAttemptNumber = (completedSessionCount ?? 0) + 1;
  const stopRowClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const renderInterviewActionItems = () => (
    <>
      {!hasCompletedSessions && (
        <>
          <DropdownMenuItem onSelect={() => handleSendEmail(false)} disabled={!candidateEmail}>
            <Mail className="w-3.5 h-3.5 me-2" />
            {t('hire.interviewLink.sendInvitation')}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => handleCopyLink(false)}>
            <Copy className="w-3.5 h-3.5 me-2" />
            {t('hire.interviewLink.copyLink')}
          </DropdownMenuItem>
        </>
      )}
      {hasCompletedSessions && hasActiveSession && (
        <>
          <DropdownMenuItem onSelect={() => handleSendEmail(false)} disabled={!candidateEmail}>
            <Mail className="w-3.5 h-3.5 me-2" />
            {t('hire.interviewLink.sendInvitation')}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => handleCopyLink(false)}>
            <Copy className="w-3.5 h-3.5 me-2" />
            {t('hire.interviewLink.copyLink')}
          </DropdownMenuItem>
        </>
      )}
      {hasCompletedSessions && canRetake && (
        <>
          {hasActiveSession && <DropdownMenuSeparator />}
          <DropdownMenuItem onSelect={() => handleSendEmail(true)} disabled={!candidateEmail}>
            <RotateCcw className="w-3.5 h-3.5 me-2" />
            {t('hire.interviewLink.newAttemptSend', { count: nextAttemptNumber })}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => handleCopyLink(true)}>
            <RotateCcw className="w-3.5 h-3.5 me-2" />
            {t('hire.interviewLink.newAttemptCopy', { count: nextAttemptNumber })}
          </DropdownMenuItem>
        </>
      )}
      {hasCompletedSessions && !canRetake && (
        <>
          {hasActiveSession && <DropdownMenuSeparator />}
          <DropdownMenuItem disabled className="text-muted-foreground text-xs">
            <Lock className="w-3.5 h-3.5 me-2" />
            {t('hire.interviewLink.retakeLimitReached', { count: maxRetakes })}
          </DropdownMenuItem>
        </>
      )}
    </>
  );

  // Entitlement block — interview limit exceeded
  const interviewBlocked = !interviewEntitlement.isLoading && !interviewEntitlement.allowed;

  if (compact) {
    if (!hasAiInterviews) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground" disabled>
              <Lock className="w-3.5 h-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top" className="text-xs max-w-[200px]">
            {interviewGovernance.message || t('hire.interviewLink.aiInterviewsUnavailable')}
          </TooltipContent>
        </Tooltip>
      );
    }
    if (interviewBlocked) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon" className="h-6 w-6 text-amber-500" disabled>
              <Lock className="w-3.5 h-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top" className="text-xs max-w-[200px]">{t('hire.interviewLink.interviewLimitReached')}</TooltipContent>
        </Tooltip>
      );
    }
    if (!hasPublishedConfig) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon" className="h-6 w-6 text-amber-500" disabled>
              <AlertTriangle className="w-3.5 h-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top" className="text-xs max-w-[200px]">{t('hire.interviewLink.questionsNotConfigured')}</TooltipContent>
        </Tooltip>
      );
    }
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 text-accent hover:text-accent"
            disabled={loading}
            onClick={stopRowClick}
            title={sent ? t('hire.interviewLink.sent') : copied ? t('hire.interviewLink.copied') : t('hire.interviewLink.interviewActions')}
          >
            {loading ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : sent ? (
              <Check className="w-3.5 h-3.5 text-success" />
            ) : copied ? (
              <Check className="w-3.5 h-3.5" />
            ) : (
              <Video className="w-3.5 h-3.5" />
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
          {renderInterviewActionItems()}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  if (!hasAiInterviews) {
    return (
      <Button variant="outline" size={size} disabled className="text-muted-foreground border-muted/30" title={interviewGovernance.message || t('hire.interviewLink.aiInterviewsUnavailable')}>
        <Lock className="w-4 h-4 me-1.5" />
        {interviewGovernance.isGlobalOff ? t('hire.interviewLink.serviceDisabled') : t('hire.interviewLink.notInPlan')}
      </Button>
    );
  }

  if (interviewBlocked) {
    return (
      <Button variant="outline" size={size} disabled className="text-amber-600 dark:text-amber-400 border-amber-300/40" title={t('hire.interviewLink.interviewLimitReachedWithUsage', { used: interviewEntitlement.used, limit: interviewEntitlement.limit })}>
        <Lock className="w-4 h-4 me-1.5" />
        {t('hire.interviewLink.limitReached')}
      </Button>
    );
  }

  if (!hasPublishedConfig && !configLoading) {
    return (
      <Button variant="outline" size={size} disabled className="text-amber-600 dark:text-amber-400 border-amber-300/40" title={t('hire.interviewLink.configureQuestionsTitle')}>
        <AlertTriangle className="w-4 h-4 me-1.5" />
        {t('hire.interviewLink.questionsNotSet')}
      </Button>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size={size}
          className="text-accent border-accent/30 hover:bg-accent/10 hover:text-accent"
          disabled={loading}
          title={sent ? t('hire.interviewLink.sent') : copied ? t('hire.interviewLink.copied') : t('hire.interviewLink.interviewActions')}
        >
          {loading ? <Loader2 className="w-4 h-4 me-1.5 animate-spin" /> : <Video className="w-4 h-4 me-1.5" />}
          {sent ? t('hire.interviewLink.sent') : copied ? t('hire.interviewLink.copied') : t('hire.interviewLink.interview')}
          <ChevronDown className="w-3.5 h-3.5 ms-1" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
        {renderInterviewActionItems()}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
